// The life-area fields of an Introduction track (topic kind "introduction"):
// area, sensitivity, permission question, bridges, the probe bank by rung,
// slots (closed values after a colon) and the short form.

import type {
  AreaSensitivity,
  AreaSlot,
  ExploreTrack,
  ProbeQuestion,
  TalkTrackContent,
} from "../../types/talkTrack";
import { ListField, SelectField, TextField } from "./fields";
import { nextUnitId } from "./format";
import { UnitCard } from "./UnitCard";

// "career_stage: student, early" <-> {name, values}
const slotLine = (s: AreaSlot) => (s.values.length ? `${s.name}: ${s.values.join(", ")}` : s.name);

function parseSlot(line: string): AreaSlot {
  const [name, values = ""] = line.split(":", 2);
  return {
    name: name.trim(),
    values: values.split(",").map((v) => v.trim()).filter(Boolean),
  };
}

export function IntroAreaFields({
  track,
  content,
  editable,
  lifeAreas,
  sensitivities,
  probeRungs,
  onPatch,
}: {
  track: ExploreTrack;
  content: TalkTrackContent;
  editable: boolean;
  lifeAreas: string[];
  sensitivities: AreaSensitivity[];
  probeRungs: string[];
  onPatch: (patch: Partial<ExploreTrack>) => void;
}) {
  const probes = track.probes ?? [];
  const sensitivity = track.sensitivity ?? "normal";
  const permission = track.permissionQuestion ?? { text: "", voiceText: "", onNo: {} };
  const patchProbe = (i: number, patch: Partial<ProbeQuestion>) =>
    onPatch({ probes: probes.map((p, j) => (j === i ? { ...p, ...patch } : p)) });

  function addProbe() {
    const rung = probeRungs[0] ?? "situation";
    const id = nextUnitId(content, `${track.id}.p`, probes.length + 1);
    onPatch({ probes: [...probes, { id, rung, text: "", voiceText: "" }] });
  }

  return (
    <>
      <div className="row-3">
        <SelectField label="Life area" editable={editable} value={track.area ?? ""} options={lifeAreas}
          emptyLabel="—" onChange={(area) => onPatch({ area: area || null })} />
        <SelectField label="Sensitivity" editable={editable} value={sensitivity} options={sensitivities}
          onChange={(value) => onPatch({ sensitivity: value as AreaSensitivity })} />
        <TextField label="Short form (core close when time is short)" editable={editable} maxWords={25}
          value={track.shortForm ?? ""} onChange={(shortForm) => onPatch({ shortForm })} />
      </div>
      {sensitivity === "permission" && (
        <div className="row-3">
          <TextField label='Permission question (with an easy "no")' editable={editable} maxWords={25}
            value={permission.text} onChange={(text) => onPatch({ permissionQuestion: { ...permission, text } })} />
          <TextField label="Short spoken version" editable={editable} value={permission.voiceText}
            onChange={(voiceText) => onPatch({ permissionQuestion: { ...permission, voiceText } })} />
          <ListField label='Facts on "no" (slot: value; empty = declined)' editable={editable} rows={2}
            value={Object.entries(permission.onNo).map(([k, v]) => `${k}: ${v}`)}
            onChange={(lines) =>
              onPatch({
                permissionQuestion: {
                  ...permission,
                  onNo: Object.fromEntries(
                    lines.map(parseSlot).map((s) => [s.name, s.values[0] ?? ""] as const).filter(([k, v]) => k && v),
                  ),
                },
              })
            } />
        </div>
      )}
      <ListField label="Bridges ({their_words} is filled from what they said)" editable={editable} rows={2}
        value={track.bridges ?? []} onChange={(bridges) => onPatch({ bridges })} />
      <ListField label="Slots (closed values after a colon, e.g. career_stage: student, early)" editable={editable}
        rows={3} value={(track.slots ?? []).map(slotLine)}
        onChange={(lines) => onPatch({ slots: lines.map(parseSlot).filter((s) => s.name) })} />
      <div className="tt-subhead">
        <span>Probes ({probes.length})</span>
      </div>
      <div className="tt-units">
        {probes.map((p, i) => (
          <UnitCard key={p.id || i} unitId={p.id} kind="Probe" title={p.text}
            onRemove={() => onPatch({ probes: probes.filter((_, j) => j !== i) })}>
            <div className="row-3">
              <SelectField label="Rung" editable={editable} value={p.rung} options={probeRungs}
                onChange={(rung) => patchProbe(i, { rung })} />
              <TextField label="Wording" editable={editable} multiline value={p.text}
                onChange={(text) => patchProbe(i, { text })} />
              <TextField label="Short spoken version" editable={editable} value={p.voiceText}
                onChange={(voiceText) => patchProbe(i, { voiceText })} />
            </div>
          </UnitCard>
        ))}
        {editable && sensitivity !== "never_explore" && (
          <button className="btn btn-sm" onClick={addProbe}>+ probe</button>
        )}
      </div>
    </>
  );
}
